import {
  RentalUnlocksGraphQL,
  RentalUnlockEventJson,
  UnlockableHardwareGraphQL,
} from '../../../hooks/rentals/query/V2';
import {
  buildUnlockRows,
  countFailedUnlockAttempts,
  IUnlockRow,
} from '../../../util/unlockEvents';

export interface IRentalUnlockGroup {
  hardware: UnlockableHardwareGraphQL;
  rows: IUnlockRow[];
  attemptCount: number;
  failedCount: number;
}

export interface IRentalUnlockSummary {
  groups: IRentalUnlockGroup[];
  failedCount: number;
}

/**
 * Pairs each unlockable hardware unit with its unlock events and computes
 * per-attempt success, so the failed-attempt count for the tab badge is
 * available without visiting the tab. Units without events still get a
 * group so their lack of attempts is visible.
 */
export function buildRentalUnlockGroups(
  data: RentalUnlocksGraphQL | null | undefined
): IRentalUnlockSummary {
  if (!data?.unlockableHardware?.length) {
    return { groups: [], failedCount: 0 };
  }

  const eventsByHardwareId = new Map<string, RentalUnlockEventJson[]>();
  (data.unlocks || []).forEach((group) => {
    eventsByHardwareId.set(group.hardware_id, group.unlocks || []);
  });

  const groups = data.unlockableHardware.map((hardware) => {
    const rows = buildUnlockRows(eventsByHardwareId.get(hardware.id) || []);

    return {
      hardware,
      rows,
      attemptCount: rows.length,
      failedCount: countFailedUnlockAttempts(rows),
    };
  });

  return {
    groups,
    failedCount: groups.reduce((total, group) => total + group.failedCount, 0),
  };
}
